<template>
    <div v-if="plan">
        <card-header title="Activate Tax Plan" icon="fa-receipt" />

        <card-body>
            <card-list>
                <user-tile
                    :card="card"
                    :user="plan.users"
                    @click="$openCard('user', {userId: plan.users.id}, card)"
                />
            </card-list>

            <card-list>
                <subheader-list-item title="Plan Summary" icon="fa-align-left" />
            </card-list>
            <data-row title="Total" class="clickable" @click="$copyText(plan.total)">
                <div class="value">{{ plan.total | currency }}</div>
            </data-row>
            <data-row title="Down" class="clickable" @click="$copyText(0)">
                <div class="value">{{ 0 | currency }}</div>
            </data-row>
            <data-row title="Payment" class="clickable" @click="$copyText(plan.monthly)">
                <div class="value">{{ monthly }}</div>
            </data-row>
            <data-row title="Deferment" class="clickable" @click="$copyText(plan.defer_on)">
                <div class="value">{{ defer }}</div>
            </data-row>
            <data-row title="Expires" class="clickable" @click="$copyText(expire)">
                <div class="value">{{ expire }}</div>
            </data-row>
            <data-row title="Term" class="clickable" @click="$copyText(plan.term)">
                <div class="value">{{ plan.term }} Month<span v-if="plan.term !== 1">s</span></div>
            </data-row>
            <card-list>
                <subheader-list-item title="Plan Actions" icon="fa-align-left" />

                <container-list-item padded v-if="plan.is_tpo && plan.is_tpo_onboarded" class="mb-3 pb-4">
                    <div class="has-background-success-light p-4 rounded">
                        <h4 class="has-text-weight-bold mb-3">
                            <i class="fas fa-check-circle mr-2 has-text-success"></i>TPO Onboarding Complete
                        </h4>
                    </div>
                </container-list-item>

                <container-list-item padded v-if="plan.is_tpo && !plan.is_tpo_onboarded && plan.tpo_onboard_hash" class="mb-3 pb-4">
                    <div class="has-background-info-light p-4 rounded">
                        <h4 class="has-text-weight-bold mb-3"><i class="fas fa-paper-plane mr-2"></i>TPO Onboarding</h4>
                        <p class="is-size-7 mb-3">Resend the onboarding link to the client via email or SMS.</p>
                        <div>
                            <b-button
                                type="is-info"
                                size="is-small"
                                @click="resendWelcomeEmail"
                                class="mr-2"
                            >
                                <i class="fas fa-envelope mr-2"></i>Resend Email
                            </b-button>
                            <b-button
                                type="is-info"
                                size="is-small"
                                @click="sendOnboardingSms"
                                :disabled="!plan.users.phone"
                            >
                                <i class="fas fa-sms mr-2"></i>Resend SMS{{ plan.users.phone ? ' to ' + plan.users.phone : '' }}
                            </b-button>
                        </div>
                    </div>
                </container-list-item>

                <container-list-item padded v-if="!plan.is_tpo || plan.is_tpo_onboarded">
                    <b-field>
                        <b-switch v-model="signed">Agreement has been signed by {{ plan.users.first_name }} {{ plan.users.last_name }}.</b-switch>
                    </b-field>
                </container-list-item>
                <container-list-item padded>
                    <b-field>
                        <b-switch type="is-danger" v-model="confirm">Allow plan to be deleted.</b-switch>
                    </b-field>
                </container-list-item>

            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column">
                    <b-button
                        type="is-danger"
                        size="is-small"
                        @click="remove"
                        :disabled="!confirm"
                    ><i class="fas fa-trash-alt mr-2"></i>Delete Plan
                    </b-button>
                </div>
                <div class="column has-text-right">
                    <b-button
                        type="is-primary"
                        size="is-small"
                        @click="submit"
                        :disabled="!signed"
                    ><i class="fas fa-check mr-2"></i>Activate Plan
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardFooter from "@/TIER/components/CardFooter";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import UserTile from "@/components/tiles/UserTile";
    import DataRow from "@/TIER/components/DataRow";

    import {client as http} from '../../http_client';
    import async from "async";
    import moment from "moment";
    import numeral from "numeral";
    import {mapActions} from "vuex";

    export default {
        components: {DataRow, UserTile, ContainerListItem, SubheaderListItem, CardList, CardFooter, CardBody, CardHeader},
        props: ['card', 'props', 'child'],
        data() {
            return {
                plan: null,
                signed: false,
                confirm: false
            };
        },
        computed: {
            monthly() {
                return numeral(this.plan.monthly).format('$0,0.00');
            },
            defer() {
                return moment.utc(this.plan.defer_on).format('MMMM Do, YYYY');
            },
            expire() {
                return moment.utc(this.plan.created_at).add(3, 'year').format('MMMM Do, YYYY');
            },
        },
        methods: {
            ...mapActions(['getPaymentPlanCounts']),
            loadPlan() {
                this.$emit('loading', true);

                async.series([
                    (cb) => {
                        http.get('payment-plans/' + this.props.planId, {force: true}).then(response => {
                            this.plan = response.data;
                            this.signed = false;
                            this.confirm = false;
                            cb();
                        }).catch(err => {
                            cb(err);
                        });
                    }
                ], err => {
                    this.$emit('loading', false);
                });
            },
            submit() {
                this.$emit('loading', true);
                http.post('payment-plans/', {planId: this.plan.id}, {force: true}).then(response => {
                    this.getPaymentPlanCounts();
                    this.$reloadCard('pending-plans');
                    this.$closeCard(this.card);
                }).catch(err => {

                });
            },
            remove() {
                if (confirm('Are you sure you want to delete this tax payment plan? This is a permanent action.')) {
                    this.$emit('loading', true);
                    http.delete('payment-plans/' + this.plan.id).then(response => {
                        this.getPaymentPlanCounts();
                        this.$reloadCard('pending-plans');
                        this.$closeCard(this.card);
                    }).catch(err => {

                    });
                }
            },
            resendWelcomeEmail() {
                if (confirm('Are you sure you want to resend the welcome email to ' + this.plan.users.email + '?')) {
                    this.$emit('loading', true);
                    http.post('payment-plans/' + this.plan.id + '/resend-welcome').then(response => {
                        this.$emit('loading', false);
                        this.$buefy.toast.open({
                            message: 'Welcome email sent successfully!',
                            type: 'is-success'
                        });
                    }).catch(err => {
                        this.$emit('loading', false);
                        this.$buefy.toast.open({
                            message: 'Failed to send welcome email',
                            type: 'is-danger'
                        });
                    });
                }
            },
            sendOnboardingSms() {
                if (confirm('Are you sure you want to send an onboarding SMS to ' + this.plan.users.phone + '?')) {
                    this.$emit('loading', true);
                    http.post('payment-plans/' + this.plan.id + '/send-onboarding-sms').then(response => {
                        this.$emit('loading', false);
                        this.$buefy.toast.open({
                            message: 'SMS sent successfully!',
                            type: 'is-success'
                        });
                    }).catch(err => {
                        this.$emit('loading', false);
                        this.$buefy.toast.open({
                            message: 'Failed to send SMS',
                            type: 'is-danger'
                        });
                    });
                }
            }
        },
        watch: {
            'props.planId': function () {
                this.loadPlan();
            }
        },
        mounted() {
            this.loadPlan();
        }
    };
</script>

<style lang="scss" scoped>
    .clickable {
        cursor: pointer;
    }
</style>
